import React, { useState } from 'react';
import { FileText, Download, Settings } from 'lucide-react';
import { DocumentGenerator } from '../../services/documents/DocumentGenerator';
import { useDeals } from '../../store/DealsContext';

export default function DocumentGeneratorComponent() {
  const { deals } = useDeals();
  const [selectedDeal, setSelectedDeal] = useState('');
  const [documentType, setDocumentType] = useState('loi');
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);

  const documentTypes = [
    { id: 'loi', name: 'Letter of Intent', icon: FileText },
    { id: 'termSheet', name: 'Term Sheet', icon: FileText },
    { id: 'dueDiligence', name: 'Due Diligence Report', icon: FileText },
    { id: 'boardPresentation', name: 'Board Presentation', icon: FileText },
    { id: 'integrationPlan', name: 'Integration Plan', icon: FileText },
    { id: 'closingChecklist', name: 'Closing Checklist', icon: FileText }
  ];

  const handleGenerate = async () => {
    if (!selectedDeal) return;

    setLoading(true);
    try {
      const deal = deals.find(d => d.id === selectedDeal);
      if (!deal) return;

      const generator = DocumentGenerator.getInstance();
      const document = await generator.generateDocument(
        documentType as any,
        deal
      );
      setPreview(document);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!preview) return;

    const blob = new Blob([preview], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${documentType}_${selectedDeal}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Document Generator</h2>
          <p className="mt-1 text-gray-600">Generate standardized M&A documents</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-sm p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Deal</label>
            <select
              value={selectedDeal}
              onChange={(e) => setSelectedDeal(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="">Select Deal</option>
              {deals.map((deal) => (
                <option key={deal.id} value={deal.id}>{deal.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Document Type</label>
            <div className="mt-2 grid grid-cols-2 gap-4">
              {documentTypes.map((type) => (
                <button
                  key={type.id}
                  onClick={() => setDocumentType(type.id)}
                  className={`p-4 border rounded-lg flex items-center space-x-2 ${
                    documentType === type.id
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 hover:border-blue-500'
                  }`}
                >
                  <type.icon className="w-5 h-5" />
                  <span className="text-sm font-medium">{type.name}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              onClick={handleGenerate}
              disabled={!selectedDeal || loading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              Generate Document
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">Preview</h3>
            {preview && (
              <button
                onClick={handleDownload}
                className="flex items-center px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded"
              >
                <Download className="w-4 h-4 mr-1" />
                Download
              </button>
            )}
          </div>
          <div className="p-6">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-sm text-gray-600">Generating document...</p>
              </div>
            ) : preview ? (
              <pre className="whitespace-pre-wrap font-mono text-sm text-gray-600">
                {preview}
              </pre>
            ) : (
              <div className="text-center py-12 text-gray-500">
                Select a deal and document type to generate a preview
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}